import { components, OptionProps, GroupBase } from 'react-select';
import { Icon, IconName } from '../Graphics';
import { ValueLabelOption } from '../Form/Select/types';
import styles from './styles.module.scss';

export type FilterChipOption<T = string> = ValueLabelOption<T>;

/**
 * Factory function to create custom option components
 * @param Renderer - React component that receives {data: Option<T>} as props
 * @returns A react-select Option component with checkbox behavior
 */
export const createCustomOption = <
  T = string,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<FilterChipOption<T>> = GroupBase<FilterChipOption<T>>,
>(
  Renderer: React.ComponentType<{ data: FilterChipOption<T> }>
) => {
  const OptionComponent = ({
    data,
    innerProps,
    innerRef,
    isSelected,
    ...props
  }: OptionProps<FilterChipOption<T>, IsMulti, Group>) => (
    <components.Option
      {...props}
      data={data}
      isSelected={isSelected}
      className={props.className ?? ''}
      innerProps={{
        ...innerProps,
        role: 'menuitem',
        'aria-label': data.label,
      }}
      innerRef={innerRef}
    >
      {isSelected ? (
        <div className={styles.selectedIcon}>
          <Icon name={IconName.Check} size="1em" strokeWidth={2.5} />
        </div>
      ) : (
        <div className={styles.notSelectedPlaceholder} />
      )}
      <span className={styles.labelContainer}>
        <Renderer data={data} />
      </span>
    </components.Option>
  );

  return OptionComponent;
};

// Generic default renderer
export const DefaultRenderer = <T,>({ data }: { data: FilterChipOption<T> }) => <>{data.label}</>;
